import React, { useEffect, useMemo, useState } from 'react';
import { Activity, ShieldOff } from 'lucide-react';
import { Card, Input, Select, EmptyState, SkeletonListRows } from '../components/ui';
import { useFirm } from '../contexts/FirmContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { listActivity } from '../lib/db';
import { classNames } from '../lib/formatters';

const ENTITY_TONE = {
  matter:       'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300',
  contact:      'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',
  invoice:      'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
  task:         'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
  expense:      'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300',
  document:     'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300',
  trust:        'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300',
  deadline:     'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300',
  employee:     'bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-300',
  subscription: 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300',
  time:         'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-300',
};

const ENTITY_DOT = {
  matter:       'bg-indigo-400',
  contact:      'bg-emerald-400',
  invoice:      'bg-amber-400',
  task:         'bg-blue-400',
  expense:      'bg-rose-400',
  document:     'bg-slate-400',
  trust:        'bg-purple-400',
  deadline:     'bg-orange-400',
  employee:     'bg-teal-400',
  subscription: 'bg-green-400',
  time:         'bg-cyan-400',
};

const LIMITS = [
  { value: '25',  label: 'Last 25' },
  { value: '50',  label: 'Last 50' },
  { value: '100', label: 'Last 100' },
  { value: '250', label: 'Last 250' },
];

export default function ActivityLogPage() {
  const { can } = usePermissions();
  const { firmId, refreshSignal } = useFirm();
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [limit, setLimit] = useState('50');

  useEffect(() => {
    if (!firmId) { setLoading(false); return; }
    setLoading(true);
    listActivity(firmId, parseInt(limit))
      .then(({ data }) => setLog(data || []))
      .finally(() => setLoading(false));
  }, [firmId, limit, refreshSignal]);

  const entityTypes = useMemo(() => {
    const types = [...new Set(log.map((e) => e.entity_type).filter(Boolean))];
    return [{ value: '', label: 'All types' }, ...types.map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))];
  }, [log]);

  const filtered = useMemo(() => log
    .filter((e) => !entityFilter || e.entity_type === entityFilter)
    .filter((e) => !q || [e.action, e.summary, e.entity_type, e.user?.first_name, e.user?.last_name]
      .some((v) => (v || '').toLowerCase().includes(q.toLowerCase()))),
    [log, entityFilter, q]);

  if (!can('activity.view')) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
        <div className="w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
          <ShieldOff size={24} className="text-slate-400" />
        </div>
        <div>
          <p className="font-medium text-slate-700 dark:text-slate-300">Access restricted</p>
          <p className="text-sm text-slate-500 mt-1">The activity log is only available to administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Activity Log</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Full audit trail of changes across your firm</p>
      </div>

      <Card>
        <div className="flex flex-col sm:flex-row gap-2 p-4 border-b border-slate-100 dark:border-slate-800">
          <Input className="flex-1" placeholder="Search actions, people…" value={q} onChange={(e) => setQ(e.target.value)} />
          <Select value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} options={entityTypes} />
          <Select value={limit} onChange={(e) => setLimit(e.target.value)} options={LIMITS} />
        </div>

        {loading ? (
          <SkeletonListRows rows={6} />
        ) : !filtered.length ? (
          <EmptyState
            icon={Activity}
            title="Nothing logged yet"
            description="Every action your team takes — creating matters, editing invoices, sending messages, granting portal access — is recorded here. Use it for accountability and to spot unusual changes."
          />
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {filtered.map((entry) => (
              <li key={entry.id} className="flex items-start gap-4 px-6 py-4 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition">
                {/* Coloured dot by entity type */}
                <div className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${ENTITY_DOT[entry.entity_type] || 'bg-slate-400'}`} />

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {entry.entity_type && (
                      <span className={classNames('text-xs font-semibold px-2 py-0.5 rounded-full', ENTITY_TONE[entry.entity_type] || ENTITY_TONE.document)}>
                        {entry.entity_type}
                      </span>
                    )}
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {entry.action}
                    </span>
                  </div>
                  {entry.summary && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{entry.summary}</p>
                  )}
                </div>

                <div className="text-right flex-shrink-0 ml-2">
                  {entry.user && (
                    <p className="text-xs font-medium text-slate-600 dark:text-slate-300">
                      {entry.user.first_name} {entry.user.last_name}
                    </p>
                  )}
                  <p className="text-xs text-slate-400">
                    {new Date(entry.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
